import {
  createContext,
  useContext,
  useEffect,
  useState,
  useRef,
  ReactNode,
} from "react";
import { useAuth } from "./AuthContext";
import { toast } from "sonner";
import { notificationAPI } from "../services/api";

interface WebSocketContextType {
  isConnected: boolean;
  notifications: any[];
  unreadCount: number;
  sendMessage: (message: any) => void;
  markAsRead: (notificationId: string) => void;
  clearNotifications: () => void;
}

const WebSocketContext = createContext<WebSocketContextType | undefined>(
  undefined,
);

export const useWebSocket = () => {
  const context = useContext(WebSocketContext);
  if (!context) {
    return {
      isConnected: false,
      notifications: [],
      unreadCount: 0,
      sendMessage: () => {},
      markAsRead: () => {},
      clearNotifications: () => {},
    };
  }
  return context;
};

interface WebSocketProviderProps {
  children: ReactNode;
}

export const WebSocketProvider = ({ children }: WebSocketProviderProps) => {
  const { user } = useAuth();
  const [isConnected, setIsConnected] = useState(false);
  const [notifications, setNotifications] = useState<any[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const reconnectAttempts = useRef(0);
  const maxReconnectAttempts = 5;

  const getWebSocketUrl = () => {
    const apiUrl =
      import.meta.env.VITE_API_URL || "http://localhost:5000/api/v1";
    const wsProtocol = apiUrl.startsWith("https") ? "wss" : "ws";
    const baseUrl = apiUrl
      .replace(/^https?:\/\//, "")
      .replace(/\/api\/v1$/, "");
    return `${wsProtocol}://${baseUrl}/ws`;
  };

  const connect = () => {
    if (!user) return;

    try {
      const wsUrl = getWebSocketUrl();
      const token = localStorage.getItem("accessToken");

      const fullUrl = `${wsUrl}?token=${token}`;

      wsRef.current = new WebSocket(fullUrl);

      wsRef.current.onopen = () => {
        setIsConnected(true);
        reconnectAttempts.current = 0;

        if (wsRef.current && token) {
          wsRef.current.send(
            JSON.stringify({
              type: "auth",
              token: token,
              userId: user.id,
            }),
          );
        }
      };

      wsRef.current.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          handleWebSocketMessage(data);
        } catch (error) {}
      };

      wsRef.current.onclose = (event) => {
        setIsConnected(false);

        if (!user) {
          return;
        }

        if (event.code === 1006 || event.code === 1005) {
          return;
        }

        if (reconnectAttempts.current < maxReconnectAttempts) {
          const delay = Math.min(
            1000 * Math.pow(2, reconnectAttempts.current),
            30000,
          );

          reconnectTimeoutRef.current = setTimeout(() => {
            reconnectAttempts.current++;
            connect();
          }, delay);
        } else {
        }
      };
    } catch (error) {
      setIsConnected(false);
    }
  };

  const disconnect = () => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    stopPolling();

    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
    setIsConnected(false);
  };

  const handleWebSocketMessage = (data: any) => {
    switch (data.type) {
      case "notification":
        handleNewNotification(data.payload);
        break;

      case "ticket_update":
        handleTicketUpdate(data.payload);
        break;

      case "assignment":
        handleAssignment(data.payload);
        break;

      case "status_change":
        handleStatusChange(data.payload);
        break;

      case "comment":
        handleNewComment(data.payload);
        break;

      case "auth_success":
        break;

      case "error":
        if (
          data.payload?.message?.includes("authentication") ||
          data.payload?.message?.includes("token")
        ) {
          if (user && localStorage.getItem("accessToken")) {
            disconnect();

            setTimeout(() => {
              if (user) {
                connect();
              }
            }, 2000);
          } else {
            disconnect();
          }
        }
        break;

      default:
    }
  };

  const handleNewNotification = (notification: any) => {
    setNotifications((prev) => [notification, ...prev]);
    setUnreadCount((prev) => prev + 1);

    toast.info(notification.title || "New Notification", {
      description: notification.message,
      duration: 5000,
    });
  };

  const handleTicketUpdate = (payload: any) => {
    toast.info("Ticket Updated", {
      description: `Ticket ${payload.ticketId} has been updated`,
      duration: 4000,
    });
  };

  const handleAssignment = (payload: any) => {
    toast.success("New Assignment", {
      description: `You've been assigned to ticket ${payload.ticketId}`,
      duration: 5000,
    });

    setNotifications((prev) => [
      {
        id: `notif-${Date.now()}`,
        userId: user?.id,
        type: "assignment",
        title: "New Ticket Assignment",
        message: `You've been assigned to ticket ${payload.ticketId}`,
        isRead: false,
        createdAt: new Date().toISOString(),
      },
      ...prev,
    ]);
    setUnreadCount((prev) => prev + 1);
  };

  const handleStatusChange = (payload: any) => {
    toast.info("Status Changed", {
      description: `Ticket ${payload.ticketId} is now ${payload.newStatus}`,
      duration: 4000,
    });
  };

  const handleNewComment = (payload: any) => {
    toast.info("New Comment", {
      description: `${payload.userName} commented on ticket ${payload.ticketId}`,
      duration: 4000,
    });
  };

  const sendMessage = (message: any) => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(message));
    }
  };

  const markAsRead = (notificationId: string) => {
    setNotifications((prev) =>
      prev.map((notif) =>
        notif.NotificationID === notificationId
          ? { ...notif, IsRead: true }
          : notif,
      ),
    );
    setUnreadCount((prev) => Math.max(0, prev - 1));
  };

  const clearNotifications = () => {
    setNotifications([]);
    setUnreadCount(0);
  };

  const fetchUnreadCount = async () => {
    try {
      const token = localStorage.getItem("accessToken");
      if (!token) return;

      const response = await notificationAPI.unreadCount();
      if (response.success || response.data != null) {
        const count =
          response.data?.count ?? response.data?.unreadCount ?? response.count;

        if (typeof count === "number") {
          setUnreadCount(count);
          return;
        }
      }

      const fallback = await notificationAPI.list(1, 100, false);
      if (fallback.success || Array.isArray(fallback)) {
        const notifList = Array.isArray(fallback)
          ? fallback
          : fallback.data?.notifications ||
            fallback.data?.items ||
            fallback.data ||
            [];
        const unread = notifList.filter(
          (n: any) => !(n.isRead ?? n.IsRead ?? false),
        ).length;
        setUnreadCount(unread);
      }
    } catch (error) {}
  };

  const startPolling = () => {
    // Poll for new notifications every 30 seconds when WebSocket is unavailable
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
    }

    pollingIntervalRef.current = setInterval(() => {
      if (!isConnected && user) {
        fetchUnreadCount();
      }
    }, 30000);
  };

  const stopPolling = () => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
  };

  useEffect(() => {
    if (user) {
      fetchUnreadCount();
      connect();

      const pollTimeout = setTimeout(() => {
        if (!isConnected) {
          startPolling();
        }
      }, 5000); // Wait 5 seconds for WebSocket

      return () => {
        clearTimeout(pollTimeout);
        stopPolling();
        disconnect();
      };
    } else {
      clearNotifications();
      disconnect();
      stopPolling();
    }
  }, [user]);

  useEffect(() => {
    if (isConnected) {
      stopPolling();
    }
  }, [isConnected]);

  useEffect(() => {
    const handleTokenRefreshed = () => {
      if (user && wsRef.current) {
        disconnect();
        setTimeout(() => connect(), 1000);
      }
    };

    window.addEventListener("token-refreshed", handleTokenRefreshed);
    return () =>
      window.removeEventListener("token-refreshed", handleTokenRefreshed);
  }, [user]);

  const value: WebSocketContextType = {
    isConnected,
    notifications,
    unreadCount,
    sendMessage,
    markAsRead,
    clearNotifications,
  };

  return (
    <WebSocketContext.Provider value={value}>
      {children}
    </WebSocketContext.Provider>
  );
};
